import { use, useEffect } from 'react';
import { FieldValues, useForm, UseFormReturn, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import ConfirmationPopupProviderContext from 'shared/components/confirmation-pop-up/providers/contexts/confirmation-popup-provider-context';
import { DateRange } from 'shared/components/date-range-picker/models/date-range';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';
import DateFormats from 'shared/constants/date-formats';

import { useTemporaryAccessContextProvider } from '../../../providers/temporary-access-provider';

type TemporaryUserDetailsPopup = {
  form: UseFormReturn<TemporaryUserPopup>;
  submit: (fieldValues: FieldValues) => Promise<void>;
  validatePeriod: () => Promise<boolean | string>;
  resetFormAndCloseTemporaryUserDetailsPopup: () => void;
};

type TemporaryUserPopup = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  organisationUnits: string[];
  role: string;
  period: DateRange | undefined;
};

const useTemporaryUserDetailsPopup = (): TemporaryUserDetailsPopup => {
  const {
    readOrganisationUnitsForTemporaryAccessData,
    readRolesForTemporaryAccessData,
    readTemporaryUserData,
    readTemporaryUserError,
    detailsViewingMode,
    createTemporaryUserIsSuccess,
    resetCreateTemporaryUserStatus,
    isTemporaryUserPeriodValid,
    createTemporaryUser,
    readRolesForTemporaryAccess,
    refresh,
    closeTemporaryUserDetailsPopup,
  } = useTemporaryAccessContextProvider();
  const { setErrorMessage } = use(ErrorMessageProviderContext);
  const { enablePopUpGuard, showDefaultConfirmationPopUp } = use(ConfirmationPopupProviderContext);
  const defaultValues = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    organisationUnits: [] as string[],
    role: '',
    period: undefined as DateRange | undefined,
  };
  const form = useForm({ mode: 'onBlur', defaultValues });
  const { isDirty } = useFormState({ control: form.control });
  const { t } = useTranslation('temporaryAccess');

  const submit = async (fieldValues: FieldValues): Promise<void> => {
    await createTemporaryUser(
      fieldValues.firstName,
      fieldValues.lastName,
      fieldValues.email,
      fieldValues.phone,
      fieldValues.organisationUnits,
      fieldValues.role,
      fieldValues.period,
    );
  };

  const validatePeriod = async (): Promise<boolean | string> => {
    const email = form.getValues('email').trim();
    const period = form.getValues('period');
    const accessOrganisationUnitIds = form.getValues('organisationUnits');

    if (await isTemporaryUserPeriodValid(email, accessOrganisationUnitIds, period as DateRange)) {
      return true;
    }

    return t('detailsPopup.body.form.period.periodInputIsInvalidText');
  };

  const resetFormAndCloseTemporaryUserDetailsPopup = (): void => {
    showDefaultConfirmationPopUp({
      shouldShow: isDirty,
      action: () => {
        closeTemporaryUserDetailsPopup();
      },
    });
  };

  useEffect(
    () => () => {
      enablePopUpGuard(false);
    },
    [],
  );

  useEffect(() => {
    enablePopUpGuard(form.formState.isDirty);
  }, [form.formState.isDirty]);

  useEffect(() => {
    form.reset({
      firstName: detailsViewingMode === 'viewing' && readTemporaryUserData ? readTemporaryUserData.firstName : '',
      lastName: detailsViewingMode === 'viewing' && readTemporaryUserData ? readTemporaryUserData.lastName : '',
      email: detailsViewingMode === 'viewing' && readTemporaryUserData ? readTemporaryUserData.email : '',
      phone: detailsViewingMode === 'viewing' && readTemporaryUserData ? readTemporaryUserData.phoneNumber : '',
      organisationUnits:
        detailsViewingMode === 'viewing' && readTemporaryUserData
          ? readTemporaryUserData.organisationUnitWithAncestors.map((item) => item.id)
          : [],
      role: detailsViewingMode === 'viewing' && readTemporaryUserData ? readTemporaryUserData.roleIdpId : '',
      period:
        detailsViewingMode === 'viewing' && readTemporaryUserData
          ? ({
              startDate: moment(readTemporaryUserData.startsOn).format(DateFormats.ISO_8601_DATE_FORMAT),
              endDate: moment(readTemporaryUserData.endsOn).format(DateFormats.ISO_8601_DATE_FORMAT),
            } as DateRange)
          : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readTemporaryUserData]);

  useEffect(() => {
    if (readTemporaryUserError) {
      setErrorMessage(readTemporaryUserError ?? undefined);
    }
  }, [readTemporaryUserError]);

  useEffect(() => {
    readRolesForTemporaryAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (detailsViewingMode === 'creation' && readRolesForTemporaryAccessData?.length === 1) {
      form.setValue('role', readRolesForTemporaryAccessData[0].idpId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readRolesForTemporaryAccessData]);

  useEffect(() => {
    if (readOrganisationUnitsForTemporaryAccessData?.length === 1) {
      form.setValue('organisationUnits', [readOrganisationUnitsForTemporaryAccessData[0].id]);
    }
  }, [readOrganisationUnitsForTemporaryAccessData]);

  useEffect(() => {
    if (createTemporaryUserIsSuccess) {
      resetCreateTemporaryUserStatus();
      closeTemporaryUserDetailsPopup();
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createTemporaryUserIsSuccess]);

  return {
    form,
    validatePeriod,
    submit,
    resetFormAndCloseTemporaryUserDetailsPopup,
  };
};

export default useTemporaryUserDetailsPopup;
